import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBot } from '../../context/BotContext';
import { botApi, isBotConfigured } from '../../services/botApi';

const {
  FiZap, FiTarget, FiCheckCircle, FiSearch, FiLoader, FiStar,
  FiTrendingUp, FiTrendingDown, FiShield, FiActivity, FiInfo,
  FiPieChart, FiGlobe, FiAlertTriangle, FiClock, FiBarChart2
} = FiIcons;

export default function TradeView() {
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [detectedToken, setDetectedToken] = useState(null);
  const [solAmount, setSolAmount] = useState('');
  const [isBuying, setIsBuying] = useState(false);

  const { balance, addPosition, addTokenToWatchlist, notify, settings } = useBot();
  const [securityCheck, setSecurityCheck] = useState(null);
  const [isRealBuying, setIsRealBuying] = useState(false);
  const [burnerSol, setBurnerSol] = useState(null);

  const isLiveMode = settings.tradingMode === 'live';

  // In live mode, "available" funds = the burner wallet's SOL (from the backend).
  useEffect(() => {
    if (!isLiveMode || !isBotConfigured()) return;
    let active = true;
    const load = async () => {
      try { const w = await botApi.getWallet(); if (active) setBurnerSol(w.sol); } catch (_e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, [isLiveMode]);

  const availSol = isLiveMode ? (burnerSol ?? 0) : balance;

  const generateInsights = useCallback((pair, ageHours, hasSocials) => {
    const liq = pair.liquidity?.usd || 0;
    const vol = pair.volume?.h24 || 0;
    const change = pair.priceChange?.h24 || 0;
    const fdv = pair.fdv || 0;
    const m5change = Math.abs(pair.priceChange?.m5 || 0);

    // Risk Analysis
    let riskLevel = 'Extreme';
    let riskColor = 'text-red-400';
    let riskBg = 'bg-red-500/10';
    let riskBorder = 'border-red-500/30';
    let riskIcon = FiShield;

    if (liq > 500000) {
      riskLevel = 'Low'; riskColor = 'text-green-400'; riskBg = 'bg-green-500/10'; riskBorder = 'border-green-500/30';
    } else if (liq > 100000) {
      riskLevel = 'Medium'; riskColor = 'text-yellow-400'; riskBg = 'bg-yellow-500/10'; riskBorder = 'border-yellow-500/30';
    } else if (liq > 20000) {
      riskLevel = 'High'; riskColor = 'text-orange-400'; riskBg = 'bg-orange-500/10'; riskBorder = 'border-orange-500/30';
    }

    let momentum = 'Neutral';
    let momColor = 'text-gray-400';
    let momIcon = FiActivity;

    if (change > 20) {
      momentum = 'Strong Bullish'; momColor = 'text-green-400'; momIcon = FiTrendingUp;
    } else if (change > 5) {
      momentum = 'Bullish'; momColor = 'text-green-400'; momIcon = FiTrendingUp;
    } else if (change < -20) {
      momentum = 'Strong Bearish'; momColor = 'text-red-400'; momIcon = FiTrendingDown;
    } else if (change < -5) {
      momentum = 'Bearish'; momColor = 'text-red-400'; momIcon = FiTrendingDown;
    }

    // Buy Pressure Score (1h transactions)
    const buys1h = pair.txns?.h1?.buys || 0;
    const sells1h = pair.txns?.h1?.sells || 0;
    const total1h = buys1h + sells1h;
    const buyPressure = total1h > 0 ? (buys1h / total1h) * 100 : 50;
    let buyPressureLabel, buyPressureColor;
    if (buyPressure > 65) {
      buyPressureLabel = 'Strong Buying'; buyPressureColor = 'text-green-400';
    } else if (buyPressure >= 50) {
      buyPressureLabel = 'Mild Buying'; buyPressureColor = 'text-yellow-400';
    } else if (buyPressure >= 35) {
      buyPressureLabel = 'Selling Pressure'; buyPressureColor = 'text-orange-400';
    } else {
      buyPressureLabel = 'Heavy Selling'; buyPressureColor = 'text-red-400';
    }

    // Volume-to-Liquidity ratio (honeypot / inactivity detection)
    const vlRatio = liq > 0 ? vol / liq : 0;
    let vlWarning = null;
    if (liq > 0 && vol > 0 && vlRatio < 0.05) {
      vlWarning = 'Very low volume vs liquidity — possible honeypot or inactive token.';
    } else if (vlRatio > 10) {
      vlWarning = 'Very high volume vs liquidity — expect significant price impact on exit.';
    }

    // Suggested stop-loss based on 5m volatility
    let suggestedSL;
    if (m5change > 5) {
      suggestedSL = 10;
    } else if (m5change > 2) {
      suggestedSL = 15;
    } else {
      suggestedSL = 20;
    }

    let strategy = '';

    // Treat missing age (null) as a new/unknown pair — fix for missing pairCreatedAt
    const isNewPair = ageHours === null || ageHours < 24;
    if (isNewPair) {
      riskLevel = 'Extreme'; riskColor = 'text-red-400'; riskBg = 'bg-red-500/10'; riskBorder = 'border-red-500/30';
      strategy += ageHours === null
        ? 'AGE UNKNOWN: Treat as new pair. Extreme rug risk. '
        : 'NEW PAIR: Extreme volatility and rug risk. Use sniper sizing only. ';
    }

    if (!hasSocials) {
      if (riskLevel !== 'Extreme') {
        riskLevel = 'High'; riskColor = 'text-orange-400'; riskBg = 'bg-orange-500/10'; riskBorder = 'border-orange-500/30';
      }
      strategy += 'No socials detected—high risk of being a honey pot. ';
    }

    if (riskLevel === 'Extreme' || riskLevel === 'High') {
      strategy += 'Expect high slippage. Keep position sizing minimal. ';
    } else {
      strategy += 'Adequate liquidity. Normal sizing rules apply. ';
    }

    if (buyPressure < 45 && total1h > 0) {
      strategy += `Selling pressure dominant (${buyPressure.toFixed(0)}% buys last 1h) — avoid entry. `;
    }

    if (momentum.includes('Bullish')) {
      strategy += 'Momentum is up. Consider a breakout entry with tight trailing stops.';
    } else if (momentum.includes('Bearish')) {
      strategy += 'Trend is down. High risk of catching a falling knife. Wait for consolidation.';
    } else {
      strategy += 'Price is chopping. Look for clear support/resistance bounces.';
    }

    return {
      riskLevel, riskColor, riskBg, riskBorder, riskIcon,
      momentum, momColor, momIcon,
      strategy,
      formattedVol: vol > 0 ? `$${(vol / 1000).toFixed(1)}k` : 'N/A',
      formattedFdv: fdv > 0 ? `$${(fdv / 1000).toFixed(1)}k` : 'N/A',
      change24h: change,
      buyPressure,
      buyPressureLabel,
      buyPressureColor,
      vlWarning,
      suggestedSL,
      total1h,
    };
  }, []);

  const selectBestPair = (pairs) => {
    const solPairs = pairs?.filter(p => {
      const quoteSymbol = p.quoteToken?.symbol?.toUpperCase();
      return p.chainId === 'solana' && p.priceNative && (quoteSymbol === 'SOL' || quoteSymbol === 'WSOL');
    }) || [];
    // Highest liquidity = most reliable price source (volume can be wash-traded)
    solPairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0));
    return solPairs[0] || null;
  };

  const buildAgeInfo = (pair) => {
    let ageText = 'Age Unknown';
    let ageHours = null;
    if (pair.pairCreatedAt) {
      const ageMs = Date.now() - pair.pairCreatedAt;
      const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
      ageHours = Math.floor(ageMs / (1000 * 60 * 60));
      const displayHours = ageHours % 24;
      ageText = ageDays > 0 ? `${ageDays}d ${displayHours}h old` : `${displayHours}h old`;
    }
    return { ageText, ageHours };
  };

  const handleSearchToken = async () => {
    if (!searchInput.trim()) return;
    setIsSearching(true);
    setDetectedToken(null);
    setSecurityCheck(null);
    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${searchInput.trim()}`);
      const data = await res.json();
      const pair = selectBestPair(data.pairs);

      if (pair) {
        const hasSocials = (pair.info?.socials?.length > 0) || (pair.info?.websites?.length > 0);
        const { ageText, ageHours } = buildAgeInfo(pair);
        const insights = generateInsights(pair, ageHours, hasSocials);

        setDetectedToken({
          symbol: pair.baseToken.symbol,
          address: pair.baseToken.address,
          liquidity: pair.liquidity?.usd ? `$${(pair.liquidity.usd / 1000).toFixed(1)}k` : 'Unknown',
          liquidityUsd: pair.liquidity?.usd || 0,
          liquidityQuoteSol: pair.liquidity?.quote || 0,
          priceNative: pair.priceNative,
          priceUsd: pair.priceUsd || null,
          insights,
          detailed: {
            txns: pair.txns?.h24 || { buys: 0, sells: 0 },
            txns1h: pair.txns?.h1 || { buys: 0, sells: 0 },
            priceChange: {
              m5: pair.priceChange?.m5 || 0,
              h1: pair.priceChange?.h1 || 0,
              h24: pair.priceChange?.h24 || 0,
            },
            hasSocials,
            ageText,
            marketCap: pair.marketCap || pair.fdv || 0
          }
        });
        setSolAmount('');

        if (settings.birdeyeEnabled && import.meta.env.VITE_BIRDEYE_API_KEY) {
          setSecurityCheck({ level: 'loading', details: [] });
          fetch(
            `https://public-api.birdeye.so/defi/token_security?address=${pair.baseToken.address}`,
            { headers: { 'X-API-KEY': import.meta.env.VITE_BIRDEYE_API_KEY, 'x-chain': 'solana' } }
          )
            .then(r => r.json())
            .then(({ data: d }) => {
              const risks = [];
              const cautions = [];
              if (d?.isHoneypot)                        risks.push('Honeypot detected');
              if (d?.nonTransferable)                   risks.push('Non-transferable token');
              if ((d?.creatorPercentage ?? 0) > 0.5)    risks.push(`Creator holds ${(d.creatorPercentage * 100).toFixed(0)}%`);
              if ((d?.top10HolderPercent ?? 0) > 0.8)   cautions.push(`Top 10 wallets hold ${(d.top10HolderPercent * 100).toFixed(0)}%`);
              if ((d?.ownerPercentage ?? 0) > 0.3)      cautions.push(`Owner holds ${(d.ownerPercentage * 100).toFixed(0)}%`);
              if (d?.freezeable)                        cautions.push('Token is freezeable');
              setSecurityCheck({
                level: risks.length ? 'risk' : cautions.length ? 'caution' : 'safe',
                details: [...risks, ...cautions],
              });
            })
            .catch(() => setSecurityCheck({ level: 'unavailable', details: [] }));
        }
      } else {
        notify('Token not found or lacks a SOL liquidity pair', 'error');
      }
    } catch (error) {
      notify('Failed to fetch token data', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // Real-time Background Updates for Searched Token
  useEffect(() => {
    let interval;
    if (detectedToken?.address) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${detectedToken.address}`);
          const data = await res.json();
          const pair = selectBestPair(data.pairs);

          if (pair) {
            const hasSocials = (pair.info?.socials?.length > 0) || (pair.info?.websites?.length > 0);
            const { ageText, ageHours } = buildAgeInfo(pair);
            const insights = generateInsights(pair, ageHours, hasSocials);

            setDetectedToken(prev => {
              if (!prev || prev.address !== pair.baseToken.address) return prev;
              return {
                ...prev,
                liquidity: pair.liquidity?.usd ? `$${(pair.liquidity.usd / 1000).toFixed(1)}k` : 'Unknown',
                liquidityUsd: pair.liquidity?.usd || 0,
                liquidityQuoteSol: pair.liquidity?.quote || 0,
                priceNative: pair.priceNative,
                priceUsd: pair.priceUsd || null,
                insights,
                detailed: {
                  txns: pair.txns?.h24 || { buys: 0, sells: 0 },
                  txns1h: pair.txns?.h1 || { buys: 0, sells: 0 },
                  priceChange: {
                    m5: pair.priceChange?.m5 || 0,
                    h1: pair.priceChange?.h1 || 0,
                    h24: pair.priceChange?.h24 || 0,
                  },
                  hasSocials,
                  ageText,
                  marketCap: pair.marketCap || pair.fdv || 0
                }
              };
            });
          }
        } catch (e) {
          // silently ignore background fetch errors
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [detectedToken?.address, generateInsights]);

  // Live buy runs entirely on the backend (burner wallet signs). The position
  // lands in Supabase and streams back via realtime — so the bot can auto-sell it.
  // Executes immediately (no confirmation step) after the same guards.
  const handleRealBuy = async () => {
    if (!solAmount || Number(solAmount) <= 0) return;
    if (!isBotConfigured()) { notify('Backend bot not configured', 'error'); return; }
    if (Number(solAmount) > availSol) { notify('Insufficient burner wallet balance', 'error'); return; }
    setIsRealBuying(true);
    try {
      const { txid } = await botApi.buy({
        tokenAddress: detectedToken.address,
        amountSol: Number(solAmount),
        slippageBps: 100,
      });
      notify(`Live Buy executed! TX: ${txid.slice(0, 8)}...`, 'success');
      setDetectedToken(null);
      setSearchInput('');
    } catch (err) {
      notify(`Live buy failed: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setIsRealBuying(false);
    }
  };

  const handleBuy = () => {
    if (!solAmount || isNaN(solAmount) || Number(solAmount) <= 0) return;
    if (Number(solAmount) > balance) {
      notify('Insufficient simulated balance', 'error');
      return;
    }
    setIsBuying(true);
    setTimeout(() => {
      addPosition(
        detectedToken.symbol,
        detectedToken.address,
        detectedToken.priceNative,
        solAmount,
        detectedToken.liquidityUsd,
        detectedToken.liquidityQuoteSol,
        detectedToken.priceUsd
      );
      setIsBuying(false);
      setDetectedToken(null);
      setSearchInput('');
    }, 1000);
  };

  const handleWatch = () => {
    addTokenToWatchlist(detectedToken.symbol, detectedToken.address, detectedToken.priceNative, detectedToken.priceUsd);
    setDetectedToken(null);
    setSearchInput('');
  };

  const formatPrice = (price) => {
    const p = parseFloat(price);
    if (!p) return '0';
    return p.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  };

  const isInsufficientFunds = Number(solAmount) > availSol;

  return (
    <div className="p-4 flex flex-col items-center justify-center min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)]">
      <AnimatePresence mode="wait">
        {!detectedToken ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#3c3c3e] shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                <SafeIcon icon={FiTarget} className="text-3xl text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Live Paper Trading</h2>
              <p className="text-sm text-gray-400 max-w-[250px] mx-auto">
                Paste a Solana Token Address below to fetch live market data and strategy insights.
              </p>
            </div>

            <div className="bg-[#1c1c1d] p-4 rounded-2xl border border-[#2c2c2e] shadow-lg">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Token Contract Address
              </label>
              <div className="relative mb-4">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchToken()}
                  placeholder="e.g. HeLp6NuQkmYB4pY..."
                  className="w-full bg-[#0a0a0b] border border-[#3c3c3e] rounded-xl py-3 pl-4 pr-10 text-white text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <button
                onClick={handleSearchToken}
                disabled={isSearching || !searchInput.trim()}
                className="w-full bg-[#2c2c2e] hover:bg-[#3c3c3e] disabled:opacity-50 text-white px-6 py-3 rounded-xl text-sm font-medium transition-colors border border-[#3c3c3e] flex items-center justify-center gap-2"
              >
                {isSearching ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSearch} />}
                {isSearching ? 'Scanning Blockchain...' : 'Load Live Token'}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="detected"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-[#1c1c1d] border border-blue-500/30 rounded-2xl p-4 shadow-[0_0_30px_rgba(59,130,246,0.1)] relative flex flex-col max-h-[85vh] overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>

            <div className="flex items-center gap-3 mb-4 shrink-0 mt-1">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center relative">
                <SafeIcon icon={FiZap} className="text-blue-400 text-xl" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#1c1c1d] animate-pulse"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg leading-tight flex items-center gap-2">
                  ${detectedToken.symbol}
                  <span className="text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30 uppercase tracking-wider">Live</span>
                </h3>
                <p className="text-blue-400 text-xs font-mono truncate max-w-[150px]">{detectedToken.address}</p>
              </div>
              <div className="text-right">
                <div className="text-white font-mono font-bold text-lg transition-colors">
                  {detectedToken.priceUsd ? `$${formatPrice(detectedToken.priceUsd)}` : formatPrice(detectedToken.priceNative)}
                </div>
                <div className="text-gray-500 text-[10px] uppercase">
                  {detectedToken.priceUsd ? 'Live Price (USD)' : 'Live Price (SOL)'}
                </div>
              </div>
            </div>

            {settings.birdeyeEnabled && securityCheck && (
              <div className={`mb-3 rounded-xl border px-3 py-2 flex items-start gap-2 text-sm shrink-0 ${
                securityCheck.level === 'risk'        ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                securityCheck.level === 'caution'     ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' :
                securityCheck.level === 'safe'        ? 'bg-green-500/10 border-green-500/30 text-green-400' :
                securityCheck.level === 'loading'     ? 'bg-gray-500/10 border-gray-500/30 text-gray-400' :
                                                        'bg-gray-500/10 border-gray-500/30 text-gray-500'
              }`}>
                <SafeIcon
                  icon={
                    securityCheck.level === 'risk' || securityCheck.level === 'caution'
                      ? FiAlertTriangle
                      : securityCheck.level === 'safe'
                        ? FiCheckCircle
                        : FiInfo
                  }
                  className="mt-0.5 flex-shrink-0"
                />
                <div>
                  <span className="font-semibold text-sm">
                    {securityCheck.level === 'loading'     ? 'Checking security...' :
                     securityCheck.level === 'unavailable' ? 'Security check unavailable' :
                     `Security: ${securityCheck.level.toUpperCase()}`}
                  </span>
                  {securityCheck.details.map((d, i) => (
                    <div key={i} className="text-xs opacity-80 mt-0.5">{d}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto scrollbar-hide space-y-4 pr-1 pb-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#0a0a0b] border border-[#2c2c2e] rounded-xl p-3 flex flex-col">
                  <span className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">Liquidity (USD)</span>
                  <span className="text-white font-mono text-sm">{detectedToken.liquidity}</span>
                </div>
                <div className="bg-[#0a0a0b] border border-[#2c2c2e] rounded-xl p-3 flex flex-col">
                  <span className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">24h Volume</span>
                  <span className="text-white font-mono text-sm">{detectedToken.insights.formattedVol}</span>
                </div>
              </div>

              <div className="bg-[#0a0a0b] border border-[#2c2c2e] rounded-xl p-3">
                <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiPieChart} className="text-purple-400" /> Detailed Token Analytics
                </h4>

                <div className="flex gap-2 mb-3">
                  <div className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border ${detectedToken.detailed.hasSocials ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                    <SafeIcon icon={detectedToken.detailed.hasSocials ? FiGlobe : FiAlertTriangle} className="text-[10px]" />
                    <span className="text-[9px] font-bold uppercase">{detectedToken.detailed.hasSocials ? 'Socials Linked' : 'No Socials'}</span>
                  </div>
                  <div className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border bg-[#1c1c1d] border-[#2c2c2e] text-gray-300">
                    <SafeIcon icon={FiClock} className="text-[10px]" />
                    <span className="text-[9px] font-bold uppercase whitespace-nowrap">
                      {detectedToken.detailed.ageText}
                    </span>
                  </div>
                </div>

                {/* 24h Order Flow */}
                <div className="mb-3">
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold">24h Order Flow</span>
                    <span className="text-[10px] font-mono font-bold text-white">
                      {detectedToken.detailed.txns.buys + detectedToken.detailed.txns.sells > 0 ? Math.round((detectedToken.detailed.txns.buys / (detectedToken.detailed.txns.buys + detectedToken.detailed.txns.sells)) * 100) : 50}% Buys
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-red-500 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${detectedToken.detailed.txns.buys + detectedToken.detailed.txns.sells > 0 ? Math.round((detectedToken.detailed.txns.buys / (detectedToken.detailed.txns.buys + detectedToken.detailed.txns.sells)) * 100) : 50}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-[8px] text-gray-500 mt-1 font-mono">
                    <span>{detectedToken.detailed.txns.buys} TXs</span>
                    <span>{detectedToken.detailed.txns.sells} TXs</span>
                  </div>
                </div>

                {/* 1h Buy Pressure */}
                {detectedToken.insights.total1h > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between items-end mb-1">
                      <span className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold">1h Buy Pressure</span>
                      <span className={`text-[10px] font-mono font-bold ${detectedToken.insights.buyPressureColor}`}>
                        {detectedToken.insights.buyPressure.toFixed(0)}% — {detectedToken.insights.buyPressureLabel}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-red-500/60 rounded-full overflow-hidden flex">
                      <div
                        className={`h-full transition-all duration-500 ${detectedToken.insights.buyPressure > 50 ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{ width: `${detectedToken.insights.buyPressure}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-[8px] text-gray-500 mt-1 font-mono">
                      <span>{detectedToken.detailed.txns1h.buys} buys</span>
                      <span>{detectedToken.detailed.txns1h.sells} sells</span>
                    </div>
                    {detectedToken.insights.buyPressure < 45 && (
                      <div className="mt-1 text-[9px] text-red-400 font-semibold flex items-center gap-1">
                        <SafeIcon icon={FiAlertTriangle} className="text-[9px]" /> Selling pressure dominant — avoid entry
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <span className="text-[9px] text-gray-500 uppercase tracking-wider font-semibold mb-1.5 block">Trend Matrix</span>
                  <div className="grid grid-cols-3 gap-2">
                    {['m5', 'h1', 'h24'].map(tf => {
                      const val = detectedToken.detailed.priceChange[tf];
                      const isPos = val >= 0;
                      return (
                        <div key={tf} className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-md p-1.5 text-center">
                          <div className="text-[8px] text-gray-500 uppercase mb-0.5">{tf}</div>
                          <div className={`text-[10px] font-mono font-bold ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                            {isPos ? '+' : ''}{val.toFixed(2)}%
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-[#2c2c2e]/30 border border-[#3c3c3e] rounded-xl p-4 transition-colors duration-500">
                <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiActivity} className="text-blue-400" /> Active Strategy Engine
                </h4>

                {/* V/L Warning */}
                {detectedToken.insights.vlWarning && (
                  <div className="mb-3 flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 rounded-lg p-2">
                    <SafeIcon icon={FiAlertTriangle} className="text-orange-400 text-[10px] mt-0.5 shrink-0" />
                    <p className="text-orange-300 text-[10px] leading-relaxed">{detectedToken.insights.vlWarning}</p>
                  </div>
                )}

                <div className="flex gap-2 mb-3">
                  <div className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-colors duration-500 ${detectedToken.insights.riskBg} ${detectedToken.insights.riskBorder} ${detectedToken.insights.riskColor}`}>
                    <SafeIcon icon={detectedToken.insights.riskIcon} className="text-[10px]" />
                    <div className="flex flex-col">
                      <span className="text-[8px] uppercase font-bold opacity-70">Risk</span>
                      <span className="text-xs font-bold leading-none">{detectedToken.insights.riskLevel}</span>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border bg-[#1c1c1d] border-[#3c3c3e] text-white">
                    <SafeIcon icon={detectedToken.insights.momIcon} className={`text-[10px] transition-colors duration-500 ${detectedToken.insights.momColor}`} />
                    <div className="flex flex-col">
                      <span className="text-[8px] uppercase font-bold text-gray-500">Momentum</span>
                      <span className={`text-xs font-bold leading-none transition-colors duration-500 ${detectedToken.insights.momColor}`}>
                        {detectedToken.insights.momentum}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 items-start bg-blue-500/5 border border-blue-500/10 rounded-lg p-2.5 mb-2">
                  <SafeIcon icon={FiInfo} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300 text-xs leading-relaxed transition-opacity duration-500">
                    {detectedToken.insights.strategy}
                  </p>
                </div>

                {/* Suggested SL hint */}
                <div className="flex items-center gap-2 bg-[#1c1c1d] border border-[#3c3c3e] rounded-lg px-2.5 py-1.5">
                  <SafeIcon icon={FiShield} className="text-orange-400 text-[10px]" />
                  <span className="text-[10px] text-gray-400">Suggested SL for this volatility:</span>
                  <span className="text-orange-400 font-mono text-[10px] font-bold ml-auto">-{detectedToken.insights.suggestedSL}%</span>
                </div>
              </div>

            </div>

            <div className="pt-3 border-t border-[#2c2c2e] shrink-0 mt-1">
              <div className="flex justify-between items-end mb-2">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {isLiveMode ? 'Live Trade Amount' : 'Paper Trade Amount'}
                </label>
                <div className="text-[10px] text-gray-500 font-mono">
                  {isLiveMode ? 'Burner' : 'Avail'}: <span className="text-white">{availSol.toFixed(2)} SOL</span>
                </div>
              </div>
              <div className="grid grid-cols-6 gap-1.5 mb-2">
                {[0.1, 0.2, 0.25, 0.5, 0.75, 1].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setSolAmount(amt.toString())}
                    className={`py-1.5 rounded-lg text-[11px] font-bold font-mono border transition-colors ${
                      Number(solAmount) === amt
                        ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                        : 'bg-[#2c2c2e] border-[#3c3c3e] text-gray-300 hover:bg-[#3c3c3e] hover:text-white'
                    }`}
                  >
                    {amt}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <input
                    type="number"
                    value={solAmount}
                    onChange={(e) => setSolAmount(e.target.value)}
                    placeholder="0.00"
                    className={`w-full bg-[#0a0a0b] border rounded-xl py-2.5 pl-3 pr-12 text-white font-mono focus:outline-none transition-colors ${isInsufficientFunds ? 'border-red-500 focus:border-red-500' : 'border-[#3c3c3e] focus:border-blue-500'}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">SOL</span>
                </div>
                <button
                  onClick={() => setSolAmount(availSol.toString())}
                  className="bg-[#2c2c2e] hover:bg-[#3c3c3e] text-blue-400 text-xs px-3 rounded-xl transition-colors border border-[#3c3c3e] font-bold"
                >
                  MAX
                </button>
              </div>

              <div className="flex flex-col gap-2">
                {!isLiveMode ? (
                  <button
                    onClick={handleBuy}
                    disabled={isBuying || !solAmount || isInsufficientFunds}
                    className={`w-full font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg ${isInsufficientFunds ? 'bg-red-500/20 text-red-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 disabled:bg-[#2c2c2e] disabled:text-gray-500 text-white shadow-blue-500/20'}`}
                  >
                    {isBuying ? <span className="animate-pulse">Executing Trade...</span>
                      : isInsufficientFunds ? 'Insufficient SOL'
                      : <><SafeIcon icon={FiCheckCircle} /> Execute Paper Buy</>}
                  </button>
                ) : (
                  <button
                    onClick={handleRealBuy}
                    disabled={isRealBuying || !solAmount || Number(solAmount) <= 0 || isInsufficientFunds}
                    className="w-full font-bold py-3 rounded-xl bg-green-600 hover:bg-green-500 disabled:bg-[#2c2c2e] disabled:text-gray-500 text-white transition-colors flex items-center justify-center gap-2 shadow-lg shadow-green-500/20"
                  >
                    {isRealBuying ? <span className="animate-pulse">Executing Live Trade...</span>
                      : isInsufficientFunds ? 'Insufficient Burner SOL'
                      : <><SafeIcon icon={FiZap} /> Buy Live (Bot-Managed)</>}
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleWatch}
                    className="flex-1 bg-[#2c2c2e] hover:bg-[#3c3c3e] text-yellow-400 text-sm font-semibold py-2.5 rounded-xl transition-colors border border-[#3c3c3e] flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiStar} /> Watch
                  </button>
                  <button
                    onClick={() => { setDetectedToken(null); setSearchInput(''); setSecurityCheck(null); }}
                    className="flex-1 bg-transparent hover:bg-[#2c2c2e] text-gray-400 text-sm font-medium py-2.5 rounded-xl transition-colors border border-[#3c3c3e]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
